import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { HomeKicker } from './HomePrimitives';
import { HomeReveal } from './HomeMotion';

const STEPS = [
  {
    title: 'Create your workspace',
    text: 'Sign up in seconds and set up your workspace in just a few clicks.',
    icon: <IconWorkspace />,
  },
  {
    title: 'Choose your modules',
    text: 'Pick the required solutions — Tasks, Documents, Snap Bill, and more.',
    icon: <IconModules />,
  },
  {
    title: 'Invite your team and start collaborating',
    text: 'Add your team, assign roles, and work together seamlessly.',
    icon: <IconTeam />,
  },
];

const gridVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.45, delayChildren: 0.1 },
  },
};

const stepVariants = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.4, 0, 0.2, 1] },
  },
};

const numVariants = {
  hidden: { scale: 0, opacity: 0 },
  show: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1], delay: 0.22 },
  },
};

const iconVariants = {
  hidden: { opacity: 0, scale: 0.82 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.18 },
  },
};

const arrowVariants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: 0.85, ease: [0.4, 0, 0.2, 1], delay: 0.4 },
  },
};

const HomeHowItWorks = () => {
  const reduce = useReducedMotion();

  return (
    <section className="home-section home-how" id="how-it-works">
      <div className="home-container">
        <HomeReveal className="home-center-head">
          <HomeKicker>How it works</HomeKicker>
          <h2 className="home-section-title">Get started with flowdo in three simple steps</h2>
          <p className="home-section-sub">
            From setting up your workspace to collaborating with your team, flowdo makes it easy to get things done.
          </p>
        </HomeReveal>

        <motion.div
          className="home-how-grid"
          initial={reduce ? false : 'hidden'}
          whileInView="show"
          viewport={{ once: true, amount: 0.28 }}
          variants={reduce ? undefined : gridVariants}
        >
          {STEPS.map((step, index) => (
            <motion.div
              key={step.title}
              className="home-how-step"
              variants={reduce ? undefined : stepVariants}
            >
              <article className="home-how-card">
                <motion.span
                  className="home-how-num"
                  variants={reduce ? undefined : numVariants}
                >
                  {index + 1}
                </motion.span>
                <motion.div
                  className="home-how-icon"
                  variants={reduce ? undefined : iconVariants}
                >
                  {step.icon}
                </motion.div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>

              {index < STEPS.length - 1 ? (
                <motion.span
                  className="home-how-arrow"
                  aria-hidden="true"
                  variants={reduce ? undefined : arrowVariants}
                  style={{ originX: 0, originY: 0.5 }}
                >
                  <svg viewBox="0 0 48 16" fill="none">
                    <path d="M1 8h30.5" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
                    <path d="M32 3.1 45.5 8 32 12.9Z" fill="currentColor" />
                  </svg>
                </motion.span>
              ) : null}
            </motion.div>
          ))}
        </motion.div>

        <HomeReveal className="home-how-note" delay={0.12}>
          <p>Credit Card not required for signing up</p>
        </HomeReveal>
      </div>
    </section>
  );
};

function IconWorkspace() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="7" cy="7" r="0.75" fill="currentColor" />
      <circle cx="9.4" cy="7" r="0.75" fill="currentColor" />
    </svg>
  );
}

function IconModules() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.2" y="3.2" width="7.2" height="7.2" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13.6" y="3.2" width="7.2" height="7.2" rx="1.4" fill="currentColor" />
      <rect x="3.2" y="13.6" width="7.2" height="7.2" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13.6" y="13.6" width="7.2" height="7.2" rx="1.4" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function IconTeam() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="7.6" r="2.35" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.1 18.2c.45-2.55 2.05-3.9 3.9-3.9s3.45 1.35 3.9 3.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="6.1" cy="9.1" r="1.85" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 18.2c.35-1.95 1.45-3.15 2.9-3.15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17.9" cy="9.1" r="1.85" stroke="currentColor" strokeWidth="1.6" />
      <path d="M20.5 18.2c-.35-1.95-1.45-3.15-2.9-3.15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export default HomeHowItWorks;
